import React, { useState } from "react";
import { Check, Loader2, TrendingUp, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/site/SEO";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

export default function Franchising() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "", state: "", investment_range: "$50k-$100k", experience: "", notes: "", honeypot: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/franchise-inquiries", form);
      toast.success("Thanks — we'll be in touch within one business day.");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <section className="max-w-2xl mx-auto py-24 px-6" data-testid={T.franchise.successCard}>
        <div className="ww-card-lg p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--ww-red)] text-white flex items-center justify-center mb-6"><Check className="h-7 w-7" /></div>
          <h1 className="font-display uppercase text-4xl text-[color:var(--ww-navy)]">Application received</h1>
          <p className="mt-3 text-slate-600">Expect an info packet in your inbox within one business day.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO title="Partner With Us — Wonder Washers" description="Bring the Wonder Washers pressure washing brand to your metro. Established brand, proven playbook, and turnkey systems." canonical="[BOOKING_URL]/franchising" />
      <section className="bg-[color:var(--ww-navy)] text-white py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">Partner With Us</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl leading-none">Own the shine in your market.</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">A proven pressure washing playbook, purpose-built software (this one), and a brand people recognize. Serious inquiries only, please.</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto py-14 md:py-20 px-6 grid md:grid-cols-3 gap-6">
        {[
          { icon: TrendingUp, t: "Proven unit economics", d: "Route density, recurring revenue, and margins baked into the model." },
          { icon: MapPin, t: "Protected territories", d: "Exclusive zip-code coverage in your metro." },
          { icon: Users, t: "Operator playbook", d: "Recruiting, training, marketing — everything you need in one place." },
        ].map(({ icon: Icon, t, d }) => (
          <div key={t} className="ww-card p-7">
            <span className="h-11 w-11 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] inline-flex items-center justify-center"><Icon className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display uppercase text-2xl text-[color:var(--ww-navy)]">{t}</h3>
            <p className="mt-2 text-slate-600 text-sm">{d}</p>
          </div>
        ))}
      </section>
      <section className="max-w-3xl mx-auto pb-20 px-6">
        <form onSubmit={submit} className="ww-card-lg p-6 md:p-10 space-y-5">
          <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)]">Request info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">First name *</label>
              <input data-testid={T.franchise.firstName} required autoComplete="given-name" className="ww-input mt-1" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Last name *</label>
              <input data-testid={T.franchise.lastName} required autoComplete="family-name" className="ww-input mt-1" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Email *</label>
              <input data-testid={T.franchise.email} required type="email" autoComplete="email" className="ww-input mt-1" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Mobile number *</label>
              <input data-testid={T.franchise.phone} required type="tel" inputMode="tel" autoComplete="tel" className="ww-input mt-1" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(541) 555-0117" />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Target market</label>
              <input className="ww-input mt-1" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City" />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">State</label>
              <input className="ww-input mt-1" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="OR" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Investment range</label>
              <select className="ww-input mt-1" value={form.investment_range} onChange={e => setForm(f => ({ ...f, investment_range: e.target.value }))}>
                <option>$50k-$100k</option><option>$100k-$250k</option><option>$250k+</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Business experience</label>
            <textarea className="ww-input mt-1 min-h-[100px]" value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} />
          </div>
          <button data-testid={T.franchise.submit} type="submit" disabled={submitting} className="ww-btn-primary inline-flex items-center gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Send inquiry
          </button>
        </form>
      </section>
    </>
  );
}
